import {
  ChartMessageContent,
  DataMessageContent,
  ErrorMessageContent,
  StatsDataCellValue,
  StatsMessage,
  TextMessageContent,
} from '../../../model/StatsAssistant'

export interface KeyedContent<T> {
  key: string
  content: T
}

export interface AssistantTurnContents {
  texts: KeyedContent<TextMessageContent>[]
  data: KeyedContent<DataMessageContent>[]
  charts: KeyedContent<ChartMessageContent>[]
  errors: KeyedContent<ErrorMessageContent>[]
}

export const contentsFromMessages = (messages: StatsMessage[]): AssistantTurnContents => {
  const contents: AssistantTurnContents = { texts: [], data: [], charts: [], errors: [] }

  messages.forEach((message) => {
    switch (message.type) {
      case 'text':
        contents.texts.push({ key: message.ulid, content: message.content })
        break
      case 'data':
        contents.data.push({ key: message.ulid, content: message.content })
        break
      case 'chart':
        contents.charts.push({ key: message.ulid, content: message.content })
        break
      case 'error':
        contents.errors.push({ key: message.ulid, content: message.content })
        break
    }
  })

  return contents
}

const isDisplayableData = (data: DataMessageContent): boolean => data.rows.length > 0

const isScalarData = (data: DataMessageContent): boolean =>
  data.columns.length === 1 && data.rows.length === 1

export const selectScalarMetrics = (
  data: KeyedContent<DataMessageContent>[],
): KeyedContent<DataMessageContent>[] =>
  data.filter(({ content }) => isScalarData(content) && isDisplayableData(content))

/** Une question → au plus un tableau brut (le dernier non vide). */
export const selectTabularData = (
  data: KeyedContent<DataMessageContent>[],
  hasChart: boolean,
): KeyedContent<DataMessageContent>[] => {
  if (hasChart) return []

  const tabular = data.filter(({ content }) => !isScalarData(content) && isDisplayableData(content))

  return tabular.slice(-1)
}

/**
 * Au plus une erreur affichée par tour. Le filtrage des erreurs internes de l'agent et la
 * traduction des timeouts sont faits côté back (CaMessageMapper) avant persistance et stream.
 */
export const selectUserFacingErrors = <T>(errors: T[]): T[] => errors.slice(-1)

const numberFormatter = new Intl.NumberFormat('fr-FR')

export const formatStatsValue = (value: StatsDataCellValue): string => {
  if (value === null) return '—'
  if (typeof value === 'number') return numberFormatter.format(value)
  return String(value)
}
